import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import apiClient from '../../api/client';

/**
 * Request a password reset link.
 *
 * The response is deliberately the same whether or not the address has an
 * account, so this page never confirms who is a TopoSDS user.
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Enter the email address you sign in with.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/api/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not send the reset link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to set a new one."
    >

          {sent ? (
            <>
              <Alert severity="success" sx={{ my: 2 }}>
                If that email has a TopoSDS account, a reset link is on its way. Check your inbox
                and your spam folder.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The link expires in an hour and can only be used once. Still nothing after a few
                minutes? Ask your administrator to confirm the address on your account.
              </Typography>
              <Button component={RouterLink} to="/login" variant="contained" fullWidth>
                Back to sign in
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter the email you sign in with and we'll send you a link to choose a new
                password.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                autoFocus
                autoComplete="email"
                disabled={submitting}
                sx={{ mb: 3 }}
              />

              <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? <CircularProgress size={24} /> : 'Send reset link'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}
    </AuthLayout>
  );
};

export default ForgotPassword;
